import React, { useState, useEffect, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
} from "reactstrap";
import { AppContext } from "../../context";
import {
  getStoreProjects,
  createStoreProject,
  normalizeStoreUserId,
  partitionStoreProjectsByOwner,
  sortStoreProjectsEarliestFirst,
  sortedStoreProjectsForBuyerAccount,
  storeQuoteDisplayLabel,
} from "../service";
import { getStoreUserDisplayName } from "../../utils";
import CreditUsageIndicator from "../creditNotes/components/CreditUsageIndicator";
import { enrichProjectListCreditMock } from "../creditNotes/fixtures";

const StoreProjects = () => {
  const { currentUser } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState("");
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const resolvedName = getStoreUserDisplayName(currentUser);
  const displayName = resolvedName === "User" ? "there" : resolvedName;
  const isOrdersPage = location.pathname === "/orders";
  const pageTitle = isOrdersPage ? "My Orders" : "My Quotes";

  const humanizeStatus = (status) => {
    if (status == null) return "";
    const raw = String(status).trim();
    if (!raw) return "";
    const s = raw.replace(/_/g, " ").toLowerCase();
    return s.replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const formatValue = (value) => {
    if (value == null || value === "") return "";
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(n)) return String(value);
    return `₹${n.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}`;
  };

  const formatShipmentDate = (value) => {
    if (!value) return "";
    const t = Date.parse(String(value));
    if (Number.isNaN(t)) return String(value);
    return new Date(t).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getStoreProjects();
      const list = enrichProjectListCreditMock(
        sortedStoreProjectsForBuyerAccount(response?.data ?? response, currentUser)
      );
      setProjects(list);
    } catch (e) {
      const raw = e?.error ?? e?.message ?? null;
      setError(
        typeof raw === "string" && raw.trim() ? raw : "Unable to load projects."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [currentUser?.id]);

  const handleCreateProject = (e) => {
    e?.preventDefault?.();
    if (!projectName.trim()) return;
    setIsLoading(true);
    setError(null);
    createStoreProject({ name: projectName.trim() })
      .then(() => loadProjects())
      .catch((e) => {
        const raw = e?.error ?? e?.message ?? null;
        setError(
          typeof raw === "string" && raw.trim()
            ? raw
            : "Unable to create project."
        );
      })
      .finally(() => {
        setProjectName("");
        setIsLoading(false);
        setShowAddModal(false);
      });
  };

  const visibleProjects = projects.filter((project) => {
    const status = String(project?.status ?? "").toLowerCase();
    if (isOrdersPage) return status !== "draft";
    return status === "draft";
  });

  const currentUserId = normalizeStoreUserId(currentUser);
  const { otherBuyerProjects, ownBuyerProjects, showBuyerQuoteDivider } =
    useMemo(() => {
      const { others, own } = partitionStoreProjectsByOwner(
        visibleProjects,
        currentUserId
      );
      return {
        otherBuyerProjects: sortStoreProjectsEarliestFirst(others),
        ownBuyerProjects: sortStoreProjectsEarliestFirst(own),
        showBuyerQuoteDivider: others.length > 0 && own.length > 0,
      };
    }, [visibleProjects, currentUserId]);

  const renderProjectCard = (project) => {
    const title = storeQuoteDisplayLabel(project, currentUserId, {
      includeOrderIdentifier: Boolean(project.order_identifier),
    });

    return (
      <div key={project.id} className="col-12 col-md-6 col-lg-4 p-0">
        <div
          className="card shadow-sm border-0 h-100"
          role="button"
          onClick={() =>
            navigate(`/projects/${project.id}`, {
              state: { project },
            })
          }
          style={{ cursor: "pointer" }}
        >
          <div className="card-body border rounded">
            <div
              className="d-grid"
              style={{
                gridTemplateColumns: "1fr auto",
                rowGap: 6,
              }}
            >
              <div
                className="fw-semibold fs-6"
                style={{ gridColumn: "1 / 2" }}
              >
                {title}
              </div>
              <div
                className="d-flex align-items-center justify-content-end gap-2"
                style={{ gridColumn: "2 / 3" }}
              >
                <span className="fw-semibold">
                  {formatValue(project.value)}
                </span>
              </div>
              <div
                className="text-muted small"
                style={{ gridColumn: "1 / 2" }}
              >
                {project?.status === "delivered" && project?.delivered_at
                  ? `Delivered on: ${formatShipmentDate(project.delivered_at)}`
                  : humanizeStatus(project.status)}
                {isOrdersPage &&
                String(project?.mode || "").toLowerCase() === "direct" ? (
                  <div className="mt-1">
                    <CreditUsageIndicator
                      credit_adjustments_total={
                        project?.credit_adjustments_total
                      }
                      credit_note_identifiers={
                        project?.credit_note_identifiers
                      }
                      compact
                    />
                  </div>
                ) : null}
              </div>
              <div
                className="text-end text-muted small"
                style={{ gridColumn: "2 / 3" }}
              >
                {Number(project.items_count || 0)} items
                {project.can_manage_project_items === false ? (
                  <span className="d-block fst-italic mt-1">View only</span>
                ) : null}
              </div>
              {project?.status !== "draft" &&
              Array.isArray(project?.shipments) &&
              project.shipments.length > 0 ? (
                <div
                  className="border-top pt-2 mt-1"
                  style={{ gridColumn: "1 / -1" }}
                >
                  <div className="text-muted small fw-semibold mb-1">
                    Shipments:
                  </div>
                  <div className="ms-3 d-flex flex-column gap-1">
                    {project.shipments.map((shipment, idx) => (
                      <div
                        key={shipment?.name ?? idx}
                        className="d-grid small"
                        style={{
                          gridTemplateColumns: "1fr 1fr 1fr 1fr",
                          columnGap: 8,
                        }}
                      >
                        <span className="fw-semibold">{shipment?.name}</span>
                        <span>
                          {formatShipmentDate(shipment?.delivery_date)}
                        </span>
                        <span>{formatValue(shipment?.price)}</span>
                        <span className="text-capitalize">
                          {shipment?.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container py-3">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="fw-bold mb-0">{pageTitle}</h2>
          </div>

          {isLoading && visibleProjects.length === 0 ? (
            <p className="text-muted mb-0">Loading projects...</p>
          ) : visibleProjects.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted mb-3">
                {isOrdersPage
                  ? `Hi ${displayName}, you don\u2019t have any orders yet.`
                  : `Hi ${displayName}, you don\u2019t have any quotes yet.`}
              </p>
            </div>
          ) : (
            <>
              <div className="row g-3">
                {otherBuyerProjects.map((project) => renderProjectCard(project))}
                {showBuyerQuoteDivider ? (
                  <div className="col-12">
                    <hr className="my-1" />
                    <p className="small text-muted fw-semibold mb-2">
                      {String(
                        currentUser?.buyer?.name ??
                          currentUser?.buyer_name ??
                          ""
                      ).trim() || "Your quotes"}
                    </p>
                  </div>
                ) : null}
                {ownBuyerProjects.map((project) => renderProjectCard(project))}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        toggle={() => {
          if (isLoading) return;
          setShowAddModal(false);
          setProjectName("");
        }}
        centered
      >
        <form onSubmit={handleCreateProject}>
          <ModalHeader
            toggle={() => {
              if (isLoading) return;
              setShowAddModal(false);
              setProjectName("");
            }}
          >
            Add Quote
          </ModalHeader>
          <ModalBody>
            <Label
              for="add-quote-name-input"
              className="form-label fw-semibold"
            >
              Quote Name
            </Label>
            <Input
              id="add-quote-name-input"
              type="text"
              className="unmask"
              placeholder="e.g. 2BHK - Koramangala"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setProjectName("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              className="fw-semibold"
              disabled={!projectName.trim() || isLoading}
            >
              {isLoading ? "Creating..." : "Create Quote"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default StoreProjects;
